import { create } from 'zustand'
import type {
  Company,
  Deal,
  Contact,
  Activity,
  Task,
  PipelineStage,
} from '../types'
import { driveService } from '../services/googleDrive'
import { generateId, createTimestamps, updateTimestamp } from '../utils/id'

interface DataState {
  companies: Company[]
  deals: Deal[]
  contacts: Contact[]
  activities: Activity[]
  tasks: Task[]
  isLoading: boolean
  error: string | null

  hydrate: () => Promise<void>

  addCompany: (company: Omit<Company, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Company>
  updateCompany: (id: string, updates: Partial<Omit<Company, 'id' | 'createdAt' | 'updatedAt'>>) => Promise<void>
  deleteCompany: (id: string) => Promise<void>

  addDeal: (deal: Omit<Deal, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>
  updateDeal: (id: string, updates: Partial<Omit<Deal, 'id' | 'createdAt' | 'updatedAt'>>) => Promise<void>
  moveDeal: (id: string, stage: PipelineStage) => Promise<void>
  deleteDeal: (id: string) => Promise<void>

  addContact: (contact: Omit<Contact, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>
  updateContact: (id: string, updates: Partial<Omit<Contact, 'id' | 'createdAt' | 'updatedAt'>>) => Promise<void>
  deleteContact: (id: string) => Promise<void>

  addActivity: (activity: Omit<Activity, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>
  deleteActivity: (id: string) => Promise<void>

  addTask: (task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>
  updateTask: (id: string, updates: Partial<Omit<Task, 'id' | 'createdAt' | 'updatedAt'>>) => Promise<void>
  toggleTask: (id: string) => Promise<void>
  deleteTask: (id: string) => Promise<void>
}

export const useDataStore = create<DataState>((set, get) => ({
  companies: [],
  deals: [],
  contacts: [],
  activities: [],
  tasks: [],
  isLoading: false,
  error: null,

  hydrate: async () => {
    set({ isLoading: true, error: null })
    try {
      const [companies, deals, contacts, activities, tasks] = await Promise.all([
        driveService.readEntity<Company[]>('companies.json'),
        driveService.readEntity<Deal[]>('deals.json'),
        driveService.readEntity<Contact[]>('contacts.json'),
        driveService.readEntity<Activity[]>('activities.json'),
        driveService.readEntity<Task[]>('tasks.json'),
      ])

      set({
        companies: companies ?? [],
        deals: deals ?? [],
        contacts: contacts ?? [],
        activities: activities ?? [],
        tasks: tasks ?? [],
        isLoading: false,
      })

      console.log('[ SYSTEM: DATA HYDRATED. READY TO WORK. ]')
    } catch (error) {
      console.error('[ SYSTEM: HYDRATION FAILED ]', error)
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load data',
      })
    }
  },

  addCompany: async (companyData) => {
    const company: Company = {
      ...companyData,
      id: generateId(),
      ...createTimestamps(),
    }

    const prevCompanies = get().companies
    set({ companies: [...prevCompanies, company] })

    try {
      await driveService.writeEntity('companies.json', get().companies)
      return company
    } catch (error) {
      set({ companies: prevCompanies })
      throw error
    }
  },

  updateCompany: async (id, updates) => {
    const prevCompanies = get().companies
    const companies = prevCompanies.map((c) =>
      c.id === id ? { ...c, ...updates, ...updateTimestamp() } : c
    )
    set({ companies })

    try {
      await driveService.writeEntity('companies.json', companies)
    } catch (error) {
      set({ companies: prevCompanies })
      throw error
    }
  },

  deleteCompany: async (id) => {
    const prevCompanies = get().companies
    const companies = prevCompanies.filter((c) => c.id !== id)
    set({ companies })

    try {
      await driveService.writeEntity('companies.json', companies)
    } catch (error) {
      set({ companies: prevCompanies })
      throw error
    }
  },

  addDeal: async (dealData) => {
    const deal: Deal = {
      ...dealData,
      id: generateId(),
      ...createTimestamps(),
    }

    const prevDeals = get().deals
    set({ deals: [...prevDeals, deal] })

    try {
      await driveService.writeEntity('deals.json', get().deals)
    } catch (error) {
      set({ deals: prevDeals })
      throw error
    }
  },

  updateDeal: async (id, updates) => {
    const prevDeals = get().deals
    const deals = prevDeals.map((d) =>
      d.id === id ? { ...d, ...updates, ...updateTimestamp() } : d
    )
    set({ deals })

    try {
      await driveService.writeEntity('deals.json', deals)
    } catch (error) {
      set({ deals: prevDeals })
      throw error
    }
  },

  moveDeal: async (id, stage) => {
    const prevDeals = get().deals
    const deals = prevDeals.map((d) =>
      d.id === id ? { ...d, stage, ...updateTimestamp() } : d
    )
    set({ deals })

    try {
      await driveService.writeEntity('deals.json', deals)
    } catch (error) {
      set({ deals: prevDeals })
      throw error
    }
  },

  deleteDeal: async (id) => {
    const prevDeals = get().deals
    const deals = prevDeals.filter((d) => d.id !== id)
    set({ deals })

    try {
      await driveService.writeEntity('deals.json', deals)
    } catch (error) {
      set({ deals: prevDeals })
      throw error
    }
  },

  addContact: async (contactData) => {
    const contact: Contact = {
      ...contactData,
      id: generateId(),
      ...createTimestamps(),
    }

    const prevContacts = get().contacts
    set({ contacts: [...prevContacts, contact] })

    try {
      await driveService.writeEntity('contacts.json', get().contacts)
    } catch (error) {
      set({ contacts: prevContacts })
      throw error
    }
  },

  updateContact: async (id, updates) => {
    const prevContacts = get().contacts
    const contacts = prevContacts.map((c) =>
      c.id === id ? { ...c, ...updates, ...updateTimestamp() } : c
    )
    set({ contacts })

    try {
      await driveService.writeEntity('contacts.json', contacts)
    } catch (error) {
      set({ contacts: prevContacts })
      throw error
    }
  },

  deleteContact: async (id) => {
    const prevContacts = get().contacts
    const contacts = prevContacts.filter((c) => c.id !== id)
    set({ contacts })

    try {
      await driveService.writeEntity('contacts.json', contacts)
    } catch (error) {
      set({ contacts: prevContacts })
      throw error
    }
  },

  addActivity: async (activityData) => {
    const activity: Activity = {
      ...activityData,
      id: generateId(),
      ...createTimestamps(),
    }

    const prevActivities = get().activities
    set({ activities: [...prevActivities, activity] })

    try {
      await driveService.writeEntity('activities.json', get().activities)
    } catch (error) {
      set({ activities: prevActivities })
      throw error
    }
  },

  deleteActivity: async (id) => {
    const prevActivities = get().activities
    const activities = prevActivities.filter((a) => a.id !== id)
    set({ activities })

    try {
      await driveService.writeEntity('activities.json', activities)
    } catch (error) {
      set({ activities: prevActivities })
      throw error
    }
  },

  addTask: async (taskData) => {
    const task: Task = {
      ...taskData,
      id: generateId(),
      ...createTimestamps(),
    }

    const prevTasks = get().tasks
    set({ tasks: [...prevTasks, task] })

    try {
      await driveService.writeEntity('tasks.json', get().tasks)
    } catch (error) {
      set({ tasks: prevTasks })
      throw error
    }
  },

  updateTask: async (id, updates) => {
    const prevTasks = get().tasks
    const tasks = prevTasks.map((t) =>
      t.id === id ? { ...t, ...updates, ...updateTimestamp() } : t
    )
    set({ tasks })

    try {
      await driveService.writeEntity('tasks.json', tasks)
    } catch (error) {
      set({ tasks: prevTasks })
      throw error
    }
  },

  toggleTask: async (id) => {
    const task = get().tasks.find((t) => t.id === id)
    if (!task) return

    const prevTasks = get().tasks
    const tasks = prevTasks.map((t) =>
      t.id === id ? { ...t, completed: !t.completed, ...updateTimestamp() } : t
    )
    set({ tasks })

    try {
      await driveService.writeEntity('tasks.json', tasks)
    } catch (error) {
      set({ tasks: prevTasks })
      throw error
    }
  },

  deleteTask: async (id) => {
    const prevTasks = get().tasks
    const tasks = prevTasks.filter((t) => t.id !== id)
    set({ tasks })

    try {
      await driveService.writeEntity('tasks.json', tasks)
    } catch (error) {
      set({ tasks: prevTasks })
      throw error
    }
  },
}))
